import { Directive, ElementRef, inject, signal } from '@angular/core';

import { MibTooltipDirective } from '../tooltip';

/**
 * Gives a row the tooltip of whatever it had to cut under the pointer, and nothing when it cut
 * nothing.
 *
 * A column is only as wide as what is left to it, so a name ends in an ellipsis sooner or later —
 * and an ellipsis with no way to read the rest is a dead end. The tooltip is the way out, and it
 * carries the **whole** text rather than the column's value, because what is cut may be inside
 * something the page wrote.
 *
 * It sits on the row and not on each cell, which is not a preference: the row's link is an anchor
 * covering the whole row, so it is the only thing the pointer ever lands on — a cell past the first
 * never sees a `mouseenter` at all. What is under the pointer is resolved from the point instead.
 *
 * It measures there rather than on render because the answer depends on the width of the table,
 * which changes with the window and with a panel opening. Measuring where the question is asked
 * costs one read of the pointed cell instead of a watcher on every one.
 */
@Directive({
  selector: '[mibClipped]',
  exportAs: 'mibClipped',
  host: {
    '(mousemove)': 'measure($event)',
    '(mouseleave)': 'clipped.set("")',
  },
})
export class MibTableClippedDirective {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tooltip = inject(MibTooltipDirective, { self: true, optional: true });

  /** The full text of what is cut under the pointer, or nothing when everything there fits. */
  readonly clipped = signal('');

  private lastCell: Element | null = null;

  protected measure(event: MouseEvent): void {
    /*
     Every element whose box holds the point, not just the topmost: the topmost is the row's own
     link, and its closest cell is the first one rather than the one being pointed at.
    */
    const cell = document
      .elementsFromPoint(event.clientX, event.clientY)
      .find((node) => node.matches('[role="gridcell"],[role="columnheader"]'));
    /* One read per cell entered rather than one per pixel: `scrollWidth` forces a layout. */
    if (cell === this.lastCell) {
      return;
    }
    this.lastCell = cell ?? null;

    const host = this.element.nativeElement;
    /* A pixel of slack: a sub-pixel layout rounds one way or the other and would read as cut. */
    const cut = cell
      ? [cell, ...cell.querySelectorAll('*')].find(
          (node) => host.contains(node) && node.scrollWidth > node.clientWidth + 1,
        )
      : undefined;
    const text = cut ? (cut.textContent ?? '').replace(/\s+/g, ' ').trim() : '';

    if (text !== this.clipped()) {
      this.clipped.set(text);
      /* The tooltip reads its text when it opens, so one already open has to be shut to follow. */
      this.tooltip?.hide();
    }
  }
}
